import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, DollarSign, Percent, ShieldCheck, Receipt } from "lucide-react";

export default function ServiceFeesPage() {
  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [diningTypes, setDiningTypes] = useState([]);
  const [form, setForm] = useState(defaultForm());

  function defaultForm() {
    return {
      name: "", amount: "", fee_type: "amount", application_type: "order",
      variable_fee: false, taxed: false, apply_to: "post_tax",
      pass_through_fee: false, is_active: true, override_pin: "",
      calc_threshold_pre_discount: false, min_threshold: "", max_threshold: "",
      auto_apply_type: "none", auto_apply_dining_type_ids: [],
    };
  }

  const fetchData = useCallback(async () => {
    try {
      const [fRes, dtRes] = await Promise.all([
        api.get("/service-fees"), api.get("/dining-types"),
      ]);
      setFees(fRes.data);
      setDiningTypes(dtRes.data.filter((d) => d.is_active));
    } catch { toast.error("Failed to load service fees"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => { setEditing(null); setForm(defaultForm()); setShowDialog(true); };
  const openEdit = (fee) => {
    setEditing(fee);
    setForm({
      name: fee.name || "", amount: fee.amount?.toString() || "",
      fee_type: fee.fee_type || "amount", application_type: fee.application_type || "order",
      variable_fee: fee.variable_fee || false, taxed: fee.taxed || false,
      apply_to: fee.apply_to || "post_tax", pass_through_fee: fee.pass_through_fee || false,
      is_active: fee.is_active !== false, override_pin: fee.override_pin || "",
      calc_threshold_pre_discount: fee.calc_threshold_pre_discount || false,
      min_threshold: fee.min_threshold?.toString() || "",
      max_threshold: fee.max_threshold?.toString() || "",
      auto_apply_type: fee.auto_apply_type || "none",
      auto_apply_dining_type_ids: fee.auto_apply_dining_type_ids || [],
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    const payload = {
      ...form,
      amount: parseFloat(form.amount) || 0,
      min_threshold: parseFloat(form.min_threshold) || 0,
      max_threshold: parseFloat(form.max_threshold) || 0,
    };
    try {
      if (editing) {
        await api.put(`/service-fees/${editing.service_fee_id}`, payload);
        toast.success("Service fee updated");
      } else {
        await api.post("/service-fees", payload);
        toast.success("Service fee created");
      }
      setShowDialog(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };

  const handleDelete = async (fee) => {
    if (!confirm(`Delete "${fee.name}"?`)) return;
    try {
      await api.delete(`/service-fees/${fee.service_fee_id}`);
      toast.success("Service fee deleted");
      fetchData();
    } catch { toast.error("Delete failed"); }
  };

  const toggleDiningType = (dtId) => {
    setForm((prev) => {
      const ids = prev.auto_apply_dining_type_ids.includes(dtId)
        ? prev.auto_apply_dining_type_ids.filter((id) => id !== dtId)
        : [...prev.auto_apply_dining_type_ids, dtId];
      return { ...prev, auto_apply_dining_type_ids: ids };
    });
  };

  if (loading) return <div className="p-6 text-slate-500" data-testid="service-fees-loading">Loading service fees...</div>;

  return (
    <div className="p-6 max-w-5xl" data-testid="service-fees-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Service Fees</h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage delivery charges, service fees, and surcharges</p>
        </div>
        <Button onClick={openCreate} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="create-service-fee-btn">
          <Plus className="h-4 w-4 mr-1" /> Add Service Fee
        </Button>
      </div>

      {fees.length === 0 ? (
        <div className="text-center py-16 bg-white border border-slate-200 rounded-lg" data-testid="no-service-fees">
          <Receipt className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 text-sm">No service fees configured yet</p>
          <Button variant="outline" size="sm" onClick={openCreate} className="mt-3"><Plus className="h-3.5 w-3.5 mr-1" /> Create One</Button>
        </div>
      ) : (
        <div className="space-y-2" data-testid="service-fees-list">
          {fees.map((fee) => (
            <div key={fee.service_fee_id} className={`bg-white border rounded-lg p-4 flex items-center gap-4 transition-colors ${fee.is_active ? "border-slate-200" : "border-slate-100 opacity-60"}`} data-testid={`service-fee-row-${fee.service_fee_id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-sm text-slate-900">{fee.name}</span>
                  {!fee.is_active && <Badge variant="outline" className="text-[10px] text-slate-400 border-slate-200">Inactive</Badge>}
                  {fee.override_pin && <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-200 bg-amber-50"><ShieldCheck className="h-2.5 w-2.5 mr-0.5" />PIN</Badge>}
                  {fee.pass_through_fee && <Badge variant="outline" className="text-[10px] text-blue-600 border-blue-200 bg-blue-50">Pass-through</Badge>}
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  <span className="font-mono font-medium text-slate-700">
                    {fee.fee_type === "percentage" ? `${fee.amount}%` : `$${(fee.amount || 0).toFixed(2)}`}
                  </span>
                  <span>Applied: {fee.application_type === "order" ? "Per Order" : "Per Item"}</span>
                  <span>{fee.apply_to === "pre_tax" ? "Pre-tax" : "Post-tax"}</span>
                  {fee.taxed && <span className="text-emerald-600">Taxable</span>}
                  {fee.auto_apply_type !== "none" && (
                    <Badge className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200" variant="outline">
                      Auto: {fee.auto_apply_type === "all_orders" ? "All orders" : "By dining type"}
                    </Badge>
                  )}
                  {fee.min_threshold > 0 && <span>Min: ${fee.min_threshold.toFixed(2)}</span>}
                  {fee.max_threshold > 0 && <span>Max: ${fee.max_threshold.toFixed(2)}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button variant="ghost" size="sm" onClick={() => openEdit(fee)} data-testid={`edit-fee-${fee.service_fee_id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(fee)} data-testid={`delete-fee-${fee.service_fee_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="service-fee-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Service Fee" : "New Service Fee"}</DialogTitle>
            <DialogDescription>Configure the fee details, thresholds, and auto-apply rules.</DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Basic Info */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Basic Info</h4>
              <div>
                <Label htmlFor="fee-name">Name *</Label>
                <Input id="fee-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Delivery Charge" className="mt-1" data-testid="fee-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="fee-amount">Amount</Label>
                  <div className="relative mt-1">
                    {form.fee_type === "percentage" ? <Percent className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" /> : <DollarSign className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />}
                    <Input id="fee-amount" type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="pl-8" data-testid="fee-amount-input" />
                  </div>
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={form.fee_type} onValueChange={(v) => setForm({ ...form, fee_type: v })}>
                    <SelectTrigger className="mt-1" data-testid="fee-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="amount">Fixed Amount ($)</SelectItem>
                      <SelectItem value="percentage">Percentage (%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Application Type</Label>
                  <Select value={form.application_type} onValueChange={(v) => setForm({ ...form, application_type: v })}>
                    <SelectTrigger className="mt-1" data-testid="fee-application-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="order">Per Order</SelectItem>
                      <SelectItem value="item">Per Item</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Apply To</Label>
                  <Select value={form.apply_to} onValueChange={(v) => setForm({ ...form, apply_to: v })}>
                    <SelectTrigger className="mt-1" data-testid="fee-apply-to-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pre_tax">Pre-tax</SelectItem>
                      <SelectItem value="post_tax">Post-tax</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Toggles */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Settings</h4>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center justify-between p-2.5 rounded-md border border-slate-200 cursor-pointer hover:bg-slate-50">
                  <span className="text-sm text-slate-700">Active</span>
                  <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="fee-active-toggle" />
                </label>
                <label className="flex items-center justify-between p-2.5 rounded-md border border-slate-200 cursor-pointer hover:bg-slate-50">
                  <span className="text-sm text-slate-700">Taxed</span>
                  <Switch checked={form.taxed} onCheckedChange={(v) => setForm({ ...form, taxed: v })} data-testid="fee-taxed-toggle" />
                </label>
                <label className="flex items-center justify-between p-2.5 rounded-md border border-slate-200 cursor-pointer hover:bg-slate-50">
                  <span className="text-sm text-slate-700">Variable Fee</span>
                  <Switch checked={form.variable_fee} onCheckedChange={(v) => setForm({ ...form, variable_fee: v })} data-testid="fee-variable-toggle" />
                </label>
                <label className="flex items-center justify-between p-2.5 rounded-md border border-slate-200 cursor-pointer hover:bg-slate-50">
                  <span className="text-sm text-slate-700">Pass-through</span>
                  <Switch checked={form.pass_through_fee} onCheckedChange={(v) => setForm({ ...form, pass_through_fee: v })} data-testid="fee-passthrough-toggle" />
                </label>
              </div>
            </div>

            {/* PIN Override */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">PIN Override</h4>
              <p className="text-xs text-slate-500">Require this PIN for a manager to waive/remove this fee at POS.</p>
              <Input value={form.override_pin} onChange={(e) => setForm({ ...form, override_pin: e.target.value })} placeholder="e.g. 1234 (leave empty for no PIN)" type="password" data-testid="fee-pin-input" />
            </div>

            {/* Thresholds */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Thresholds</h4>
              <label className="flex items-center justify-between p-2.5 rounded-md border border-slate-200 cursor-pointer hover:bg-slate-50">
                <span className="text-sm text-slate-700">Calculate threshold pre-discount</span>
                <Switch checked={form.calc_threshold_pre_discount} onCheckedChange={(v) => setForm({ ...form, calc_threshold_pre_discount: v })} data-testid="fee-threshold-prediscount-toggle" />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Min Threshold ($)</Label>
                  <Input type="number" step="0.01" min="0" value={form.min_threshold} onChange={(e) => setForm({ ...form, min_threshold: e.target.value })} placeholder="0.00" className="mt-1" data-testid="fee-min-threshold-input" />
                </div>
                <div>
                  <Label>Max Threshold ($)</Label>
                  <Input type="number" step="0.01" min="0" value={form.max_threshold} onChange={(e) => setForm({ ...form, max_threshold: e.target.value })} placeholder="No limit" className="mt-1" data-testid="fee-max-threshold-input" />
                </div>
              </div>
            </div>

            {/* Auto Apply */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Auto Apply</h4>
              <div>
                <Label>Auto Apply Type</Label>
                <Select value={form.auto_apply_type} onValueChange={(v) => setForm({ ...form, auto_apply_type: v })}>
                  <SelectTrigger className="mt-1" data-testid="fee-auto-apply-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (Manual only)</SelectItem>
                    <SelectItem value="all_orders">All Orders</SelectItem>
                    <SelectItem value="by_dining_type">By Dining Type</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.auto_apply_type === "by_dining_type" && (
                <div>
                  <Label className="mb-2 block">Dining Types</Label>
                  <div className="flex flex-wrap gap-2">
                    {diningTypes.map((dt) => (
                      <button key={dt.dining_type_id} type="button"
                        onClick={() => toggleDiningType(dt.dining_type_id)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${form.auto_apply_dining_type_ids.includes(dt.dining_type_id)
                          ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]"
                          : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                        }`}
                        data-testid={`fee-dining-type-${dt.dining_type_id}`}
                      >
                        {dt.name}
                      </button>
                    ))}
                    {diningTypes.length === 0 && <p className="text-xs text-slate-400">No dining types configured</p>}
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} data-testid="fee-cancel-btn">Cancel</Button>
            <Button onClick={handleSave} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="fee-save-btn">
              {editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
